/* eslint-disable max-len */
/* eslint-disable import/no-cycle */
import { TimeSlot } from '@brands/Dashboard/PatientForms/PatientForm/utils/ProviderAvailabilityCard/Components/TimeSlotComponent';
import { Meta } from '@brands/services/communication/types/Meta';
import { uniqBy } from 'lodash';
import { useEffect, useState } from 'react';

import {
  ListAvailableCareAdvisorParams,
  listAvailableCareAdvisors,
} from '../services/identity/listAvailableCareAdvisors';
import { UserProfile } from '../services/identity/types/UserProfile';
import { isValidDateFormat } from '../Utils/validation';

interface IUseUsers {
  providers: IListAvailableCareAdvisor[];
  dataLoading: boolean;
  loadNextPage: () => Promise<void>;
  refreshCareAdvisors: () => void;
}

export interface IListAvailableCareAdvisor {
  created_at: string;
  id: number;
  sync_time_slots: string[];
  sync_time_intervals: TimeSlot[];
  async_time_slots: string[];
  async_time_intervals: TimeSlot[];
  updated_at: string;
  user: UserProfile;
  in_network?: boolean;
}

export default function useAvailableCareAdvisors(
  user_type: string,
  params: Pick<
    ListAvailableCareAdvisorParams,
    | 'limit'
    | 'date'
    | 'tz'
    | 'language'
    | 'state'
    | 'patient_id'
    | 'case_id'
    | 'case_type'
    | 'synchronous'
    | 'asynchronous'
    | 'care_advisor_id'
  >,
): IUseUsers {
  const [providers, setCareAdvisors] = useState<IListAvailableCareAdvisor[]>([]);
  const [dataLoading, setDataLoading] = useState<boolean>(false);
  const [firstPageMeta, setFirstPageMeta] = useState<Meta | null>(null);
  const [lastPageMeta, setLastPageMeta] = useState<Meta | null>(null);

  const loadNextPage = async (): Promise<void> => {
    if (!lastPageMeta?.has_next_page) {
      return;
    }
    const { meta, nodes } = await listAvailableCareAdvisors(user_type, {
      ...params,
    });
    setLastPageMeta(meta);
    if (!firstPageMeta) {
      setFirstPageMeta(meta);
    }
    setCareAdvisors((prevCareAdvisors) => uniqBy([...prevCareAdvisors, ...nodes], 'id'));
  };

  useEffect(() => {
    let canceled = false;
    setDataLoading(true);
    listAvailableCareAdvisors(user_type, params).then((usersList) => {
      if (!canceled) {
        setCareAdvisors(usersList.nodes);
        setFirstPageMeta(usersList.meta);
        setLastPageMeta(usersList.meta);
        setDataLoading(false);
      }
    });

    return (): void => {
      canceled = true;
    };
  }, [params]);

  const refreshCareAdvisors = (): void => {
    setDataLoading(true);
    listAvailableCareAdvisors(user_type, params).then((usersList) => {
      setCareAdvisors(usersList.nodes);
      setDataLoading(false);
    });
  };

  return {
    providers,
    dataLoading,
    loadNextPage,
    refreshCareAdvisors,
  };
}
